<template>
  <ValidationObserver
    ref="analysisTypesFormObserver"
  >
    <div>
      <p class="titleModal">
        {{ $t('samples.labels.analysisTypes') }}
      </p>
      <!-- contract selector -->
      <VAutocomplete
        v-if="isErpActive"
        v-model="selectedContracts"
        class="custom-autocomplete"
        withCustomItemText
        outlined
        multiple
        :label="$t('forms.labels.contract')"
        :items="displayedContracts"
        itemText="uniqueId"
        data-cy="selectGroup"
      >
        <template v-slot:selection="{ item, index }">
          <!-- Customizing how each selected item is shown without a close button -->
          <v-chip
            :key="index"
            class="chip--select-multi"
            :inputValue="true"
            closeable="false"
          >
            {{ item.uniqueId }}
          </v-chip>
        </template>
        <template v-slot:customItemText="{ item, on, attrs }">
          <v-list-item
            v-bind="attrs"
            v-on="on"
          >
            <v-list-item-action>
              <v-checkbox
                :inputValue="isContractItemSelected(item.item)"
                @change="toggleContractSelection(item.item)"
              />
            <!-- contract selector custom option list -->
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ `${$t('contracts.contractNumber')}: ${item.item.contractNumber}` }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-content>
              <v-list-item-title>{{ `${$t('contracts.erpCode')}: ${item.item.erpCode}` }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-content>
              <v-list-item-title>{{ `${$t('contracts.availableQty')}: ${item.item.availableQty}` }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </VAutocomplete>
      <!-- analysis groups selector -->
      <VAutocomplete
        outlined
        :value="selectedAnalysisGroups"
        :label="$t('samples.labels.analysisGroups')"
        :items="data.analysisGroups"
        multiple
        :disabled="isDisabledAnalysisFields()"
        data-cy="selectGroup"
        @change="onChangeAnalysisGroups"
      />
      <!-- analysis types selector -->
      <VAutocomplete
        outlined
        multiple
        :disabled="isDisabledAnalysisFields()"
        itemValue="key"
        :items="displayedTypes"
        :label="$t('samples.labels.analysisTypes')"
        :rules="{ required: (!isSampleType(sampleTypes.DEPOSIT) && !isSampleType(sampleTypes.OTHER)) }"
        :value="data.analysisTypes"
        data-cy="selectTypes"
        @change="onChangeAnalysisTypes"
      />
      <!-- billable and po -->
      <v-row style="align-items: center">
        <v-col
          cols="12"
          sm="3"
        >
          <v-checkbox
            v-model="data.billable"
            dense
            :disabled="isContractSelected()"
            color="primary"
            class="mt-0"
            :label="$t('forms.labels.billable')"
            @change="onChangeBillable"
          />
        </v-col>
        <v-col
          cols="12"
          sm="3"
        >
          <VTextField
            v-model="data.poNumber"
            :disabled="isContractSelected()"
            :label="$t('forms.labels.poNumber')"
            outlined
            :rules="{ required: true, max: 200 }"
            @change="onChangePo"
          />
        </v-col>
      </v-row>
    </div>
  </ValidationObserver>
</template>
<script>
import { difference, uniq } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import VAutocomplete from '@/components/shared/widgets/forms/VuetifyInputs/VAutocomplete.vue';
import sampleTypes from '@/constants/sampleTypes';
import { TYPE_CONTRACT } from '@/aquassist-shared/constants/types/features';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import { isFeatureActiveForTeam } from '@/utilities/featureValidator';
import roles from '@/constants/roles';
import { ValidationObserver } from 'vee-validate';

/**
 * Executes a function after a specified delay, ensuring that the function
 * is not called multiple times within the delay period.
 *
 * @param {Function} func - The function to be debounced.
 * @param {number} delay - The delay in milliseconds before the debounced
 *     function is executed.
 * @returns {Function} - A debounced function that will execute after the
 *     specified delay.
 */
function debounce(func, delay) {
    let debounceTimer;
    // eslint-disable-next-line func-names
    return function (...args) {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => { func.apply(this, args); }, delay);
    };
}

export default {
    components: {
        ValidationObserver,
        VAutocomplete,
        VTextField,
    },
    props: {
        mutableValue: {
            type: Object,
            default: null,
        },
        selectedGroups: {
            type: Array,
            default: () => ([]),
        },
    },
    data() {
        return {
            data: {
                analysisTypes: [],
                analysisGroups: [],
                poNumber: this.$t('nonApplicable'),
                billable: false,
            },
            selectedAnalysisGroups: [],
            selectedContracts: [],
            contractsList: [],
            selectedSiteId: null,
            features: [],
            isErpActive: false,
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'getSample',
            'teamMainLang',
            'siteId',
            'team',
            'hasRole',
        ]),
        displayedTypes() {
            let legioAnalysis = [];
            if (this.isSampleType(sampleTypes.LEGIONELLOSIS) || this.isSampleType(sampleTypes.LEGIONELLA_CULTRURE)) {
                legioAnalysis = this.mutableValue.analysisTypesByLegionella.map(type => ({
                    id: type.id,
                    key: type.key,
                    name: this.nameAnalysisLegionella(type),
                }));
            }
            const simplesAnalysis = this.mutableValue.analysisTypes.map(type => ({
                id: type.id,
                key: type.key,
                name: this.nameAnalysis(type),
            }));
            return [...legioAnalysis, ...simplesAnalysis];
        },
        displayedContracts() {
            return this.contractsList;
        },
        sampleTypes() {
            return sampleTypes;
        },
    },
    watch: {
        /**
         * Converts the selected contract uniqueIds to contract objects and updates the analysis groups.
         *
         * @param {Array|string} newValue - The new value of selected contracts. Can be an array of uniqueIds or an object.
         * @return {void}
         */
        selectedContracts(newValue) {
            if (typeof newValue[0] === 'object') {
                return;
            }
            const selectedContractObjects = newValue.map(
                uniqueId => this.contractsList.find(contract => contract.uniqueId === uniqueId),
            ).filter(contract => contract !== undefined);
            if (selectedContractObjects.length === 0) {
                this.mutableValue.billable = false;
                this.data.poNumber = this.$t('nonApplicable');
            }
            this.onChangeAnalysisGroups(selectedContractObjects);
        },
    },
    async created() {
        if (this.selectedGroups) {
            this.selectedAnalysisGroups = this.selectedGroups.map(group => group.id);
        }
        this.data.billable = this.mutableValue.sample.billable;
        this.data.analysisTypes = this.mutableValue.sample.analysisTypes ? this.mutableValue.sample.analysisTypes : [];
        this.data.analysisTypes.forEach(analysisType => {
            this.handleAddedAnalysisType(analysisType, this.data.analysisTypes, false);
        });
        this.data.analysisGroups = this.mutableValue.analysisGroups;
        this.mutableValue.analysisGroups.forEach((analysis, index) => {
            this.data.analysisGroups[index].name = analysis.name[this.userLang] || analysis.name[this.teamMainLang];
        });
        this.data.poNumber = this.mutableValue.isLab && this.mutableValue.poNumber ? this.mutableValue.poNumber : this.$t('nonApplicable');
        if (this.getSample.template) {
            const { selectedAnalysisGroups, analysisTypes, billable, poNumber } = this.getSample;
            this.selectedAnalysisGroups = selectedAnalysisGroups;
            this.data.analysisTypes = analysisTypes;
            this.data.billable = billable;
            this.data.poNumber = poNumber;
        }
        if (this.siteId) {
            this.selectedSiteId = this.siteId;
        }
        // Check if the ERP feature is active for the team
        await this.getFeatures();
        this.isErpActive = isFeatureActiveForTeam(this.team, this.features, TYPE_CONTRACT);
        if (this.isErpActive) {
            await this.getContracts();
        }
        // Keep this at then end of the created hook
        if (this.mutableValue.sample.selectedContracts.length > 0) {
            this.selectedContracts = this.mutableValue.sample.selectedContracts;
        }
    },
    methods: {
        ...mapActions({
            getContractsInfo: 'getContractsInfo',
            getFeaturesInfo: 'getFeaturesInfo',
            resetContractsInfo: 'resetContractsInfo',
        }),
        isDisabledAnalysisFields() {
            // Disable the field for operators
            if (this.hasRole(roles.ROLE_OPERATOR.value)) {
                return true;
            }
            // List of other allowed roles
            const rolesAllowedWithoutContracts = [
                roles.ADMINISTRATOR.value,
                roles.MANAGER.value,
                roles.SALES_REPRESENTATIVE.value,
                roles.LABORATORY_MANAGER.value,
                roles.LABORATORY_MICROBIOLOGIST.value,
                roles.LABORATORY_CHEMIST.value,
                roles.LABORATORY_TECHNICIAN.value,
                roles.SALES_MANAGER.value,
                roles.SERVICE_TECHNICIAN.value,
            ];
            const hasRequiredRole = rolesAllowedWithoutContracts.some(role => this.hasRole(role));
            // Enable the field if user has one of the specified roles and a contract is not selected
            if (hasRequiredRole && !this.isContractSelected()) {
                return false;
            }
            // Default to disabling the field
            return true;
        },
        /**
         * Checks if an item is selected.
         *
         * @param {Object} item - The item to check if selected.
         * @returns {boolean} - True if the item is selected, otherwise false.
         */
        isContractItemSelected(item) {
            let isSelected = false;
            if (this.selectedContracts.uniqueId) {
                isSelected = this.selectedContracts.some(contract => contract.uniqueId === item.uniqueId);
            } else {
                isSelected = this.selectedContracts.some(contract => contract === item.uniqueId);
            }
            return isSelected;
        },
        /**
         * Checks if there are any contracts selected.
         *
         * @return {boolean} Returns true if at least one contract is selected, otherwise false.
         */
        isContractSelected() {
            return !!(this.selectedContracts && this.selectedContracts.length > 0);
        },
        /**
         * Toggles the selection of a single contract.
         *
         * @param {object} contract - The contract to toggle the selection for.
         * @return {void}
         */
        toggleContractSelection(contract) {
            // we create a uniqueId for the contract //
            const uniqueId = contract.uniqueId || `${contract.contractNumber}-${contract.erpCode}`;
            const index = this.selectedContracts.findIndex(c => (typeof c === 'object' ? c.uniqueId : c) === uniqueId);
            if (index > -1) {
                // Contract is already selected, remove it
                this.selectedContracts.splice(index, 1);
            } else {
                // Contract is not selected, add the uniqueId
                this.selectedContracts.push(uniqueId);
                this.data.poNumber = contract.poNumber || this.$t('nonApplicable');
                this.data.billable = false;
            }
            // Reset when no contracts are selected
            if (this.selectedContracts.length === 0) {
                this.data.poNumber = this.$t('nonApplicable');
                this.getContracts(); // refresh contract
                this.data.analysisTypes = [];
            }
            const analysisGroupId = this.findGroupIdByERPCode(contract.erpCode);
            if (analysisGroupId) {
                this.onChangeAnalysisGroups([analysisGroupId]);
            }
            this.filterContractsByPo(contract);
            this.$emit('change', this.selectedContracts);
        },
        filterContractsByPo(contract) {
            if (!contract || !contract.poNumber) {
                return;
            }
            this.contractsList = this.contractsList.filter(contractInList => contractInList.poNumber === contract.poNumber);
        },
        findGroupIdByERPCode(erpCode) {
            // Ensure the analysis groups array and erpCode are defined
            if (!this.mutableValue || !this.mutableValue.analysisGroups || !erpCode) {
                return null;
            }
            // Find the group that matches the ERPCode
            const foundGroup = this.mutableValue.analysisGroups.find(group => group.ERPCode === erpCode);

            // If a group is found, return its id; otherwise return null
            return foundGroup ? foundGroup.id : null;
        },
        /**
         * Detect if the user added or removed an analysis group from the list.
         * We trust the autocomplete, always giving us a valid group ID or ERPCode.
         *
         * @param {Array} newVal - New list of selected groups
         */
        // eslint-disable-next-line func-names
        onChangeAnalysisGroups: debounce(function (newVal) {
            let analysisGroupId;
            let matchingGroup = [];
            if (newVal.length > 0 && this.isMongoId(newVal[0])) {
                matchingGroup = this.mutableValue.analysisGroups.filter(group => newVal.includes(group.id));
            } else if (this.isContractSelected()) {
                newVal.forEach(val => {
                    // Only attempt to find a matching group if erpCode is defined
                    if (val.erpCode !== undefined) {
                        const foundGroup = this.mutableValue.analysisGroups.find(group => group.ERPCode === val.erpCode);
                        if (foundGroup) {
                            matchingGroup.push(foundGroup);
                        }
                    }
                });
            }
            if (matchingGroup) {
                analysisGroupId = matchingGroup.map(group => group.id);
            } else {
                analysisGroupId = [];
            }
            const oldVal = this.selectedAnalysisGroups;
            const addedItems = difference(analysisGroupId, oldVal);
            if (addedItems.length > 0) {
                this.handleAddedAnalysisGroup(addedItems[0]);
            } else {
                const removedItems = difference(oldVal, analysisGroupId);
                if (removedItems.length > 0) {
                    this.handleRemovedAnalysisGroup(removedItems[0], analysisGroupId);
                }
            }
            if (analysisGroupId) {
                this.selectedAnalysisGroups = analysisGroupId;
            } else {
                this.selectedAnalysisGroups = '';
            }
            this.updateData();
        }, 300),
        isMongoId(id) {
            const regex = /^[0-9a-fA-F]{24}$/;
            return regex.test(id);
        },
        /**
         * @param {Array} newVal - New list of selected types
         */
        onChangeAnalysisTypes(newVal) {
            this.data.analysisTypes = newVal;
            this.updateData();
        },
        /**
         * Automatically select all the types from the selected group.
         *
         * @param {Array} newGroupId - id of the new group added by the user
         */
        handleAddedAnalysisGroup(newGroupId) {
            let newGroup;
            if (newGroupId) {
                newGroup = this.mutableValue.analysisGroups.find(availableGroup => availableGroup.id === newGroupId);
                this.data.analysisTypes = uniq(this.data.analysisTypes.concat(newGroup.types));
            } else {
                newGroup = [];
            }
            this.updateData();
        },
        /**
         * Detect if the associated types from the removed group, are still associated to
         * one of the "still" selected groups. If a type has no associated group, we remove it.
         *
         * @param {object} removedGroupId - id of the group deleted by the user
         * @param {array} newSelectedGroups
         */
        handleRemovedAnalysisGroup(removedGroupId, newSelectedGroups) {
            // Get data of the removed group
            const removedGroup = this.mutableValue.analysisGroups.find(group => group.id === removedGroupId);
            // Combine all the types from selected groups into one array of types
            const analysesFromSelectedGroups = newSelectedGroups.reduce((result, selectedGroup) => {
                const foundGroup = this.mutableValue.analysisGroups.find(group => group.id === selectedGroup);
                if (foundGroup) {
                    return result.concat(foundGroup.types);
                }
                return result;
            }, []);
            // Determine types to be removed
            const typesToBeRemoved = removedGroup.types.filter(typeKey => !analysesFromSelectedGroups.includes(typeKey));
            this.data.analysisTypes = this.data.analysisTypes.filter(type => !typesToBeRemoved.includes(type));
            this.updateData();
        },
        /**
         * Detect if we need to automatically select a group,
         * if all its associated types have been manually selected.
         *
         * @param {String} newTypeKey - id of the new analysis type added by the user
         * @param {Array} newSelectedTypes - New list of the selected types Object, including the new one
         * @param findMatchedGroups
         */
        handleAddedAnalysisType(newTypeKey, newSelectedTypes, findMatchedGroups = true) {
            const { selectedAnalysisGroups } = this;
            let matchedGroups = [];
            if (findMatchedGroups) {
                matchedGroups = this.mutableValue.analysisGroups.filter(group => (
                    selectedAnalysisGroups.indexOf(group.id) === -1
                    && group.types.indexOf(newTypeKey) !== -1
                    && difference(group.types, newSelectedTypes).length === 0
                ));
            }
            this.selectedAnalysisGroups = selectedAnalysisGroups.concat(...matchedGroups.map(group => group));
        },
        onChangeBillable() {
            if (!this.data.billable) {
                this.data.poNumber = this.$t('nonApplicable');
            } else {
                this.data.poNumber = '';
            }
            this.updateData();
        },
        onChangePo() {
            this.updateData();
        },
        async updateData() {
            this.$emit('updateData', {
                ...this.data,
                billable: this.data.billable,
                poNumber: this.data.poNumber,
                selectedAnalysisGroups: this.selectedAnalysisGroups,
                selectedContracts: this.selectedContracts.map(contract => contract.uniqueId),
            });
            await this.$refs.analysisTypesFormObserver.validate({ silent: true });
            this.$emit('inputChange', this.$refs.analysisTypesFormObserver.validate);
        },
        isSampleType(type) {
            return this.getSample.type === type;
        },
        nameAnalysisLegionella(type) {
            return `${type.name[this.userLang]} ( ${type.measureUnit[this.userLang]} )`
                    || ` ${type.name[this.teamMainLang]} ( ${type.measureUnit[this.teamMainLang]} )`;
        },
        nameAnalysis(type) {
            if (type.measureUnit) {
                return `${type.name[this.userLang]} ( ${type.measureUnit[this.userLang]} )`
                || ` ${type.name[this.teamMainLang]} ( ${type.measureUnit[this.teamMainLang]} )`;
            }
            return type.name[this.userLang] || type.name[this.teamMainLang];
        },
        /**
         * Retrieves the contracts from the store and copies them to the component's data.
         * Adds additional properties such as erpCode, availableQty, and analysis to each contract.
         *
         * @returns {Promise<void>} A promise that resolves once the features are retrieved.
         */
        async getContracts() {
            await this.getContractsInfo({ teamId: this.team.id, siteId: this.siteId });
            const { contracts } = this.$store.state.contracts;
            const finalContracts = [];
            contracts.forEach(contract => {
                contract.laboratoryAnalysisRequest.forEach(analysis => {
                    // Create a new object with properties from the contract and specific analysis details
                    finalContracts.push({
                        ...contract,
                        erpCode: analysis.erpCode,
                        availableQty: analysis.availableQty,
                        uniqueId: `${contract.contractNumber}-${analysis.erpCode}`,
                    });
                });
            });
            // eslint-disable-next-line no-underscore-dangle
            this.contractsList = finalContracts.filter(contract => contract.siteNumber._id === this.selectedSiteId && contract.availableQty > 0);
        },
        /**
         * Retrieves the features from the store.
         *
         * @returns {Promise<void>} A promise that resolves once the features are retrieved.
         */
        async getFeatures() {
            await this.getFeaturesInfo();
            const { features } = this.$store.state.features;
            this.features = features;
        },
    },
};
</script>
